/**
 * Ficha de cliente (Gestión → Clientes).
 *
 * Reúne lo que en el panel anterior eran tres acciones distintas de la fila
 * —*Editar* (`/clients/edit/:id`), las del **portal** (`/cliente/activar_portal`,
 * `desactivar_portal`, `regenerar_password_portal`) y *Eliminar*
 * (`/cliente/delete_cliente`)— y añade las **placas activas** del cliente, que
 * antes sólo se veían en el modal «ver placas» del listado.
 *
 * Reglas que se conservan:
 * - `tipo_doc` y `numero_doc` no se editan: en el formulario original eran de
 *   sólo lectura (`tipo_doc` además estaba comentado en el `update`).
 * - El porcentaje de retención sólo se guarda si el cliente aplica retenciones.
 * - Regenerar la contraseña exige que el portal esté activo y la contraseña se
 *   muestra **una sola vez** (en la base se guarda con `bcrypt`).
 * - La baja es física y sólo se permite si el cliente no tiene facturas,
 *   boletas, vales ni créditos (la API responde `409` con el detalle).
 *
 * Pendiente (con candado): placas y descuentos por cliente, vales y créditos.
 */

import { Building2, Car, KeyRound, Lock, Power, PowerOff, Trash2, UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import {
  useAccionPortal,
  useActualizarCliente,
  useClienteFicha,
  useEliminarCliente,
} from '../clientes.api';
import type { AccionPortal, Cliente, EdicionCliente } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tipos de cliente del formulario original. */
const TIPOS_CLIENTE = ['Cliente Normal', 'Cliente Distribuidor'];

/** Fecha del panel anterior (`d/m/Y H:i`), o un guion. */
function fechaCorta(valor: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Valores del formulario a partir del cliente. */
function formularioDe(cliente: Cliente): EdicionCliente {
  return {
    tipo: cliente.tipo ?? TIPOS_CLIENTE[0],
    nombre: cliente.nombre ?? '',
    nombre_comercial: cliente.nombre_comercial ?? '',
    direccion: cliente.direccion ?? '',
    telefono: cliente.telefono ?? '',
    correo: cliente.correo ?? '',
    estado: cliente.estado,
    estado_sunat: cliente.estado_sunat ?? '',
    condicion_sunat: cliente.condicion_sunat ?? '',
    withholding: cliente.withholding,
    withholding_percentage: cliente.withholding_percentage,
  };
}

interface Props {
  /** Cliente abierto (o `null` si el diálogo está cerrado). */
  id: number | null;
  open: boolean;
  onClose: () => void;
  /** Mensaje de éxito que muestra el listado. */
  onAviso: (mensaje: string) => void;
  /** Abre la ficha directamente en modo edición (acción «Editar» de la fila). */
  editarInicial?: boolean;
}
export function ClienteFichaDialog({ id, open, onClose, onAviso, editarInicial = false }: Props) {
  const ficha = useClienteFicha(open ? id : null);
  const actualizar = useActualizarCliente();
  const portal = useAccionPortal();
  const eliminar = useEliminarCliente();

  const [editando, setEditando] = useState(false);
  const [formulario, setFormulario] = useState<EdicionCliente>({});
  const [error, setError] = useState<string | null>(null);
  const [credenciales, setCredenciales] = useState<{ usuario?: string; password: string } | null>(
    null,
  );
  const [confirmarBaja, setConfirmarBaja] = useState(false);

  const cliente = ficha.data?.cliente ?? null;
  const bloqueos = ficha.data?.bloqueos ?? null;

  // Cuando se abre con «Editar», entra una sola vez en edición al cargar la
  // ficha; si el usuario pulsa «Cancelar», ya no se vuelve a abrir el formulario.
  const autoEditado = useRef(false);

  useEffect(() => {
    if (!open) {
      autoEditado.current = false;

      return;
    }

    if (!editarInicial || autoEditado.current || !cliente) return;

    autoEditado.current = true;
    setFormulario(formularioDe(cliente));
    setEditando(true);
  }, [open, editarInicial, cliente]);

  const cambiar = <C extends keyof EdicionCliente>(campo: C, valor: EdicionCliente[C]) => {
    setFormulario((previo) => ({ ...previo, [campo]: valor }));
  };

  const guardar = () => {
    if (!cliente) return;

    setError(null);
    actualizar.mutate(
      { id: cliente.id, datos: formulario },
      {
        onSuccess: () => {
          setEditando(false);
          onAviso(`Cliente #${cliente.id} actualizado.`);
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el cliente.'),
      },
    );
  };

  const ejecutarPortal = (accion: AccionPortal) => {
    if (!cliente) return;

    setError(null);
    setCredenciales(null);
    portal.mutate(
      { id: cliente.id, accion },
      {
        onSuccess: (respuesta) => {
          if (respuesta.password) {
            setCredenciales({ usuario: respuesta.usuario, password: respuesta.password });
          }

          onAviso(respuesta.mensaje);
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo actualizar el portal.'),
      },
    );
  };

  const darDeBaja = () => {
    if (!cliente) return;

    setError(null);
    eliminar.mutate(cliente.id, {
      onSuccess: (respuesta) => {
        setConfirmarBaja(false);
        onClose();
        onAviso(respuesta.mensaje);
      },
      onError: (fallo) => {
        setConfirmarBaja(false);
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo eliminar el cliente.');
      },
    });
  };

  // `tipo_doc` no se edita (en el formulario original era de sólo lectura).
  const esRuc = cliente?.tipo_doc === '6' || cliente?.tipo_doc === 'RUC';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        size="lg"
        title={cliente ? `Cliente · ${cliente.nombre ?? ''}` : 'Cliente'}
        description={
          cliente
            ? `${cliente.tipo_doc === '6' ? 'RUC' : 'DNI'} ${cliente.numero_doc ?? ''} · #${cliente.id}`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cerrar
            </Button>
            {cliente ? (
              editando ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditando(false)}>
                    Cancelar
                  </Button>
                  <Button size="sm" loading={actualizar.isPending} onClick={guardar}>
                    Guardar cambios
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<UserRound className="size-4" />}
                  onClick={() => {
                    setFormulario(formularioDe(cliente));
                    setError(null);
                    setEditando(true);
                  }}
                >
                  Editar ficha
                </Button>
              )
            ) : null}
          </>
        }
      >
        {ficha.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : null}

        {ficha.isError ? (
          <Alert variant="error" title="No se pudo abrir el cliente">
            {ficha.error instanceof ApiError ? ficha.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {cliente ? (
          <div className="space-y-4">
            {error ? (
              <Alert variant="error" title="No se pudo completar la acción">
                {error}
              </Alert>
            ) : null}

            {credenciales ? (
              <Alert variant="warning" title="Credenciales del portal (no se volverán a mostrar)">
                <span className={ICONO_EN_ALERTA}>
                  <KeyRound className="size-4" aria-hidden="true" />
                  Usuario <strong>{credenciales.usuario ?? cliente.numero_doc}</strong> · contraseña{' '}
                  <strong className="font-mono">{credenciales.password}</strong>
                </span>
              </Alert>
            ) : null}

            {editando ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Tipo de documento</span>
                  <Select className={CONTROL} value={esRuc ? '6' : '1'} disabled>
                    <option value="1">DNI</option>
                    <option value="6">RUC</option>
                  </Select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Número de documento</span>
                  <Input value={cliente.numero_doc ?? ''} disabled />
                </label>

                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Tipo de cliente</span>
                  <Select
                    className={CONTROL}
                    value={formulario.tipo ?? TIPOS_CLIENTE[0]}
                    onChange={(evento) => cambiar('tipo', evento.target.value)}
                  >
                    {TIPOS_CLIENTE.map((opcion) => (
                      <option key={opcion} value={opcion}>
                        {opcion}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Estado</span>
                  <Select
                    className={CONTROL}
                    value={formulario.estado === false ? '0' : '1'}
                    onChange={(evento) => cambiar('estado', evento.target.value === '1')}
                  >
                    <option value="1">Activado</option>
                    <option value="0">Desactivado</option>
                  </Select>
                </label>

                <Input
                  label="Nombre"
                  value={formulario.nombre ?? ''}
                  onChange={(evento) => cambiar('nombre', evento.target.value)}
                />

                <Input
                  label="Nombre comercial"
                  value={formulario.nombre_comercial ?? ''}
                  onChange={(evento) => cambiar('nombre_comercial', evento.target.value)}
                />

                <Input
                  label="Dirección"
                  value={formulario.direccion ?? ''}
                  onChange={(evento) => cambiar('direccion', evento.target.value)}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Teléfono"
                    inputMode="numeric"
                    value={formulario.telefono ?? ''}
                    onChange={(evento) =>
                      cambiar('telefono', evento.target.value.replace(/\D/g, '').slice(0, 15))
                    }
                  />
                  <Input
                    label="Correo"
                    type="email"
                    value={formulario.correo ?? ''}
                    onChange={(evento) => cambiar('correo', evento.target.value)}
                  />
                </div>

                <Input
                  label="Estado contribuyente (SUNAT)"
                  placeholder="ACTIVO"
                  value={formulario.estado_sunat ?? ''}
                  onChange={(evento) => cambiar('estado_sunat', evento.target.value)}
                />

                <Input
                  label="Condición contribuyente (SUNAT)"
                  placeholder="HABIDO"
                  value={formulario.condicion_sunat ?? ''}
                  onChange={(evento) => cambiar('condicion_sunat', evento.target.value)}
                />

                {esRuc ? (
                  <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                    <label className="flex flex-col gap-1.5 text-sm">
                      <span className="font-medium text-ink-700">Aplican retenciones</span>
                      <Select
                        className={CONTROL}
                        value={formulario.withholding ? '1' : '0'}
                        onChange={(evento) => cambiar('withholding', evento.target.value === '1')}
                      >
                        <option value="0">No aplican</option>
                        <option value="1">Sí aplican</option>
                      </Select>
                    </label>

                    <Input
                      label="Porcentaje de retención"
                      type="number"
                      min={0}
                      max={100}
                      disabled={!formulario.withholding}
                      value={String(formulario.withholding_percentage ?? 0)}
                      onChange={(evento) =>
                        cambiar('withholding_percentage', Number(evento.target.value))
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <dl className="grid gap-4 sm:grid-cols-2">
                  {[
                    ['Tipo de cliente', cliente.tipo ?? '—'],
                    ['Nombre comercial', cliente.nombre_comercial ?? '—'],
                    ['Dirección', cliente.direccion ?? '—'],
                    ['Teléfono', cliente.telefono ?? '—'],
                    ['Correo', cliente.correo ?? '—'],
                    ['Estado', cliente.estado ? 'Activado' : 'Desactivado'],
                    ['Estado contribuyente', cliente.estado_sunat ?? '—'],
                    ['Condición contribuyente', cliente.condicion_sunat ?? '—'],
                    [
                      'Retenciones',
                      cliente.withholding
                        ? `Sí · ${cliente.withholding_percentage}%`
                        : 'No aplican',
                    ],
                    ['Registrado', fechaCorta(cliente.created_at)],
                  ].map(([etiqueta, valor]) => (
                    <div key={etiqueta}>
                      <dt className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        {etiqueta}
                      </dt>
                      <dd className="text-sm text-ink-800">{valor}</dd>
                    </div>
                  ))}
                </dl>

                <section className="rounded-card border border-ink-100 bg-ink-50/40 p-4">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                    <Car className="size-4 text-brand-500" aria-hidden="true" />
                    Placas activas
                    <Badge variant="neutral">{cliente.cantidad_placas}</Badge>
                  </h3>

                  {cliente.placas.length === 0 ? (
                    <p className="text-sm text-ink-500">Este cliente no tiene placas activas.</p>
                  ) : (
                    <ul className="grid gap-2 sm:grid-cols-2">
                      {cliente.placas.map((placa) => (
                        <li
                          key={placa.id}
                          className="rounded-control border border-ink-100 bg-white px-3 py-2 text-sm"
                        >
                          <span className="font-semibold text-ink-900">{placa.numero_placa}</span>
                          <span className="block text-xs text-ink-500">
                            {placa.descripcion_placa || 'Sin descripción'}
                            {placa.tipo_placa ? ` · ${placa.tipo_placa}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="mt-3 flex items-start gap-2 text-xs text-ink-500">
                    <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    La gestión de placas con descuentos (`/cliente/placas/:id`) está pendiente de
                    migrar.
                  </p>
                </section>

                <section className="rounded-card border border-ink-100 bg-white p-4">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                    <Building2 className="size-4 text-brand-500" aria-hidden="true" />
                    Portal del cliente
                  </h3>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={cliente.portal_activo ? 'success' : 'neutral'}>
                      {cliente.portal_activo ? 'Portal activo' : 'Portal inactivo'}
                    </Badge>
                    <span className="text-xs text-ink-500">
                      {cliente.portal_activo
                        ? `Último acceso: ${fechaCorta(cliente.portal_ultimo_acceso)}`
                        : 'El cliente no puede entrar al portal.'}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {cliente.portal_activo ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<KeyRound className="size-4" />}
                          loading={portal.isPending}
                          onClick={() => ejecutarPortal('regenerar')}
                        >
                          Regenerar contraseña
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<PowerOff className="size-4" />}
                          loading={portal.isPending}
                          onClick={() => ejecutarPortal('desactivar')}
                        >
                          Desactivar portal
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        leftIcon={<Power className="size-4" />}
                        loading={portal.isPending}
                        onClick={() => ejecutarPortal('activar')}
                      >
                        Activar portal
                      </Button>
                    )}
                  </div>
                </section>

                <section className="rounded-card border border-rose-100 bg-rose-50/40 p-4">
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-800">
                    <Trash2 className="size-4" aria-hidden="true" />
                    Dar de baja al cliente
                  </h3>

                  {bloqueos && !bloqueos.eliminar ? (
                    <p className="text-sm text-rose-800">
                      No se puede eliminar: tiene{' '}
                      <strong>
                        {bloqueos.detalle
                          .filter((item) => item.total > 0)
                          .map((item) => `${item.total} ${item.etiqueta}`)
                          .join(', ')}
                      </strong>{' '}
                      a su nombre.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-rose-800">
                        La baja libera el documento para volver a registrarlo. No se puede
                        deshacer.
                      </p>
                      <Button
                        variant="danger"
                        size="sm"
                        className="mt-3"
                        leftIcon={<Trash2 className="size-4" />}
                        onClick={() => setConfirmarBaja(true)}
                      >
                        Eliminar cliente
                      </Button>
                    </>
                  )}
                </section>
              </>
            )}
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={confirmarBaja}
        title={`¿Eliminar a ${cliente?.nombre ?? 'este cliente'}?`}
        description={`Se borrará el cliente #${cliente?.id ?? ''} (${cliente?.numero_doc ?? ''}) del padrón.`}
        confirmLabel="Eliminar"
        tone="danger"
        loading={eliminar.isPending}
        onConfirm={darDeBaja}
        onClose={() => setConfirmarBaja(false)}
      />
    </>
  );
}
